import type { WordCloudItem } from '@core/models/word-cloud-item.model';

export interface PreparedWordCloudItem extends WordCloudItem {
  ratio: number;
  normalized: number;
  color: string;
}

export interface WordCloudPoint {
  x: number;
  y: number;
}

export interface WordCloudBox {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  color: string;
  highlighted: boolean;
}

export interface WordCloudLayoutOptions {
  width: number;
  height: number;
  minFontSize: number;
  maxFontSize: number;
  previous: ReadonlyMap<string, WordCloudPoint>;
  measureText: (text: string, fontSize: number) => number;
}

const SCALE_ATTEMPTS = 5;
const SCALE_STEP = 0.85;
const WORD_SHRINK_ATTEMPTS = 3;
const WORD_SHRINK_STEP = 0.85;
const START_JITTER = 0.08;
export const WORD_CLOUD_PALETTE: readonly string[] = [
  '#1d4ed8',
  '#e11d48',
  '#d97706',
  '#047857',
  '#7c3aed',
  '#ea580c',
  '#0e7490',
  '#a21caf',
];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function hashString(text: string): number {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function seededRandom(text: string): () => number {
  let state = hashString(text);
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function prepareWordCloudItems(words: readonly WordCloudItem[]): PreparedWordCloudItem[] {
  const valid = words
    .map((word) => ({ ...word, text: word.text.trim() }))
    .filter((word) => word.text !== '' && Number.isFinite(word.weight) && word.weight > 0)
    .sort((a, b) => (b.weight !== a.weight ? b.weight - a.weight : a.id.localeCompare(b.id)));

  if (valid.length === 0) {
    return [];
  }

  const minWeight = Math.min(...valid.map((word) => word.weight));
  const maxWeight = Math.max(...valid.map((word) => word.weight));

  return valid.map((word) => {
    const ratio =
      maxWeight === minWeight
        ? 0.5
        : clamp((word.weight - minWeight) / (maxWeight - minWeight), 0, 1);
    const random = seededRandom(word.id);

    return {
      ...word,
      ratio,
      normalized: Math.sqrt(ratio),
      color: WORD_CLOUD_PALETTE[Math.floor(random() * WORD_CLOUD_PALETTE.length)],
    };
  });
}

export function selectVisibleWordCloudItems(
  items: readonly PreparedWordCloudItem[],
  maxWords: number
): PreparedWordCloudItem[] {
  const limit = Math.max(1, maxWords);
  const visible = items.slice(0, limit);
  const highlighted = items.find((item) => item.highlighted);

  if (!highlighted || visible.includes(highlighted)) {
    return visible;
  }

  return [...visible.slice(0, limit - 1), highlighted];
}

function overlaps(a: WordCloudBox, b: WordCloudBox): boolean {
  return (
    Math.abs(a.x - b.x) * 2 < a.width + b.width && Math.abs(a.y - b.y) * 2 < a.height + b.height
  );
}

function insideArea(box: WordCloudBox, width: number, height: number): boolean {
  return (
    box.x - box.width / 2 >= 0 &&
    box.y - box.height / 2 >= 0 &&
    box.x + box.width / 2 <= width &&
    box.y + box.height / 2 <= height
  );
}

function placeBox(
  box: WordCloudBox,
  start: WordCloudPoint,
  placed: readonly WordCloudBox[],
  width: number,
  height: number,
  direction: number
): boolean {
  const aspect = width / height;
  const maxRadius = Math.hypot(width, height);

  for (let step = 0; ; step++) {
    const t = step * 0.1 * direction;
    if (Math.abs(t) > maxRadius) {
      return false;
    }
    box.x = start.x + aspect * t * Math.cos(t);
    box.y = start.y + t * Math.sin(t);

    if (insideArea(box, width, height) && !placed.some((other) => overlaps(box, other))) {
      return true;
    }
  }
}

function layoutAt(
  items: readonly PreparedWordCloudItem[],
  options: WordCloudLayoutOptions,
  scale: number
): { placed: WordCloudBox[]; dropped: number } {
  const { width, height, minFontSize, maxFontSize, previous, measureText } = options;
  const fontTop = Math.min(maxFontSize, height * 0.3, width * 0.18) * scale;
  const fontBottom = Math.min(minFontSize * scale, fontTop * 0.5);
  const placed: WordCloudBox[] = [];
  let dropped = 0;

  const measure = (text: string, fontSize: number) => {
    const padding = 4 + fontSize * 0.08;
    return {
      width: measureText(text, fontSize) + padding * 2,
      height: fontSize * 1.15 + padding,
    };
  };

  const ordered = [
    ...items.filter((item) => previous.has(item.id)),
    ...items.filter((item) => !previous.has(item.id)),
  ];

  for (const item of ordered) {
    const random = seededRandom(item.id);
    const prior = previous.get(item.id);
    const start = prior
      ? { x: prior.x * width, y: prior.y * height }
      : {
          x: width / 2 + (random() - 0.5) * width * START_JITTER,
          y: height / 2 + (random() - 0.5) * height * START_JITTER,
        };
    const direction = random() < 0.5 ? 1 : -1;

    let fontSize = fontBottom + item.normalized * (fontTop - fontBottom);
    let size = measure(item.text, fontSize);
    if (size.width > width) {
      fontSize *= width / size.width;
      size = measure(item.text, fontSize);
    }

    let isPlaced = false;
    for (let attempt = 0; attempt <= WORD_SHRINK_ATTEMPTS && !isPlaced; attempt++) {
      const box: WordCloudBox = {
        id: item.id,
        text: item.text,
        x: start.x,
        y: start.y,
        width: size.width,
        height: size.height,
        fontSize,
        color: item.color,
        highlighted: item.highlighted ?? false,
      };
      if (placeBox(box, start, placed, width, height, direction)) {
        placed.push(box);
        isPlaced = true;
      } else {
        fontSize *= WORD_SHRINK_STEP;
        size = measure(item.text, fontSize);
      }
    }

    if (!isPlaced) {
      dropped++;
    }
  }

  return { placed, dropped };
}

export function layoutWordCloud(
  items: readonly PreparedWordCloudItem[],
  options: WordCloudLayoutOptions
): WordCloudBox[] {
  if (items.length === 0 || options.width < 1 || options.height < 1) {
    return [];
  }

  let result = { placed: [] as WordCloudBox[], dropped: items.length };
  let scale = 1;
  for (let attempt = 0; attempt < SCALE_ATTEMPTS; attempt++) {
    result = layoutAt(items, options, scale);
    if (result.dropped === 0) {
      break;
    }
    scale *= SCALE_STEP;
  }
  return result.placed;
}

export function toRelativePositions(
  boxes: readonly WordCloudBox[],
  width: number,
  height: number
): Map<string, WordCloudPoint> {
  return new Map(boxes.map((box) => [box.id, { x: box.x / width, y: box.y / height }]));
}
